import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { fetcher } from "../../state/axios";
import { ENTITY_LOCK_TYPE } from "../../types/visibility";

type Params = {
  /** Interval in seconds between retry checks */
  retryInterval?: number;
  /** Maximum number of retries */
  maxRetries?: number;
  /** Callback to execute when the cluster is up */
  onDataWarehouseUp(): void;
  /**
   * Whether ClickHouse is the primary warehouse for this company.
   * Must be derived from company data (not JWT) so it reflects the server-side
   * CompanyMigrator state. When true, Databricks checks are skipped entirely.
   */
  isClickhouseEnabled: boolean;
  /**
   * Gate flag — warehouse checks do not fire until this is true.
   * Set to Boolean(companyData) so we know isClickhouseEnabled is accurate
   * before deciding which warehouse(s) to warm up.
   */
  shouldStart: boolean;
};

type WarehouseStatus = {
  databricks: "pending" | "ready" | "error";
  clickhouse: "pending" | "ready" | "error";
};

/** Custom hook for using functions at data warehouses (Databricks + ClickHouse) */
const useDataWarehouseConfigurations = ({
  retryInterval = 5,
  maxRetries = 4,
  onDataWarehouseUp,
  isClickhouseEnabled,
  shouldStart,
}: Params) => {
  const [retry, setRetry] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isDataWarehouseUp, setIsDataWarehouseUp] = useState(false);
  const [isWarehouseLoading, setIsWarehouseLoading] = useState(true);
  const [isWarehouseLoadError, setIsWarehouseLoadError] = useState(false);

  // Track individual warehouse statuses
  const [warehouseStatus, setWarehouseStatus] = useState<WarehouseStatus>({
    databricks: "pending",
    clickhouse: "pending",
  });

  // Track if onDataWarehouseUp has been called to prevent duplicate calls
  const hasCalledOnUp = useRef(false);

  // Check if all warehouses are ready or acceptable
  const checkAllWarehousesReady = useCallback(
    (status: WarehouseStatus) => {
      if (isClickhouseEnabled) {
        // ClickHouse is the primary (and only) warehouse — Databricks is not checked
        if (status.clickhouse === "ready" && !hasCalledOnUp.current) {
          hasCalledOnUp.current = true;
          setIsDataWarehouseUp(true);
          setIsWarehouseLoading(false);
          onDataWarehouseUp();
        } else if (status.clickhouse === "error") {
          setIsWarehouseLoading(false);
          setIsWarehouseLoadError(true);
        }
      } else {
        // Databricks is the primary warehouse; ClickHouse errors are acceptable
        const databricksOk = status.databricks === "ready";
        const clickhouseOk =
          status.clickhouse === "ready" || status.clickhouse === "error";

        if (databricksOk && clickhouseOk && !hasCalledOnUp.current) {
          hasCalledOnUp.current = true;
          setIsDataWarehouseUp(true);
          setIsWarehouseLoading(false);
          onDataWarehouseUp();
        } else if (
          status.databricks === "error" &&
          status.clickhouse === "error"
        ) {
          setIsWarehouseLoading(false);
          setIsWarehouseLoadError(true);
        }
      }
    },
    [isClickhouseEnabled, onDataWarehouseUp]
  );

  // Databricks warehouse warm-up — skipped entirely when ClickHouse is the primary warehouse.
  // Also gated on shouldStart so we never fire before knowing which warehouse is in use.
  const { mutate: checkDatabricksStatus, error: databricksError } = useSWR(
    shouldStart && !isClickhouseEnabled ? "/api/v1/databricks/warehouse/start" : null,
    (url) => fetcher(url, { method: "POST" }),
    {
      revalidateOnMount: true,
      shouldRetryOnError: false,
      onSuccess(data) {
        if (data.status) {
          setWarehouseStatus((prev) => {
            const newStatus = { ...prev, databricks: "ready" as const };
            checkAllWarehousesReady(newStatus);
            return newStatus;
          });
        } else {
          // Databricks returned status=false, retry
          if (retryCount <= maxRetries) {
            setRetry(true);
            setRetryCount((c) => c + 1);
          } else {
            setWarehouseStatus((prev) => {
              const newStatus = { ...prev, databricks: "error" as const };
              checkAllWarehousesReady(newStatus);
              return newStatus;
            });
          }
        }
      },
      onError() {
        setWarehouseStatus((prev) => {
          const newStatus = { ...prev, databricks: "error" as const };
          checkAllWarehousesReady(newStatus);
          return newStatus;
        });
      },
    }
  );

  // ClickHouse warehouse warm-up — gated on shouldStart so we fire only once
  // isClickhouseEnabled is confirmed accurate.
  const { mutate: checkClickhouseStatus, error: clickhouseError } = useSWR(
    shouldStart ? "/api/v1/clickhouse/warehouse/warm_up" : null,
    (url) => fetcher(url, { method: "POST" }),
    {
      revalidateOnMount: true,
      shouldRetryOnError: false,
      onSuccess(data) {
        if (data.status === "ready") {
          setWarehouseStatus((prev) => {
            const newStatus = { ...prev, clickhouse: "ready" as const };
            checkAllWarehousesReady(newStatus);
            return newStatus;
          });
        } else {
          // ClickHouse not ready - mark as error (it's acceptable to continue)
          setWarehouseStatus((prev) => {
            const newStatus = { ...prev, clickhouse: "error" as const };
            checkAllWarehousesReady(newStatus);
            return newStatus;
          });
        }
      },
      onError() {
        // ClickHouse error is acceptable - some entities may not use ClickHouse
        setWarehouseStatus((prev) => {
          const newStatus = { ...prev, clickhouse: "error" as const };
          checkAllWarehousesReady(newStatus);
          return newStatus;
        });
      },
    }
  );

  const vantageLockedStatus = useMemo(() => {
    let isEnabled = false;
    let config = {
      lockType: ENTITY_LOCK_TYPE.UNAUTHORIZED,
      marketingHtmlUrl: "https://sapaad.com/",
    };
    // Check Databricks error for locked status (primary warehouse)
    if (databricksError?.response?.status === 423) {
      isEnabled = true;
      config.lockType =
        databricksError?.response?.data?.lock_type ??
        ENTITY_LOCK_TYPE.UNAUTHORIZED;
      if (
        databricksError?.response?.data?.lock_type ===
        ENTITY_LOCK_TYPE.MARKETING_MESSAGE
      ) {
        config.marketingHtmlUrl =
          databricksError?.response?.data?.marketing_html_url;
      }
    }
    return { isEnabled, config };
  }, [databricksError]);

  const restartDataWarehouseCluster = useCallback(() => {
    setRetry(false);
    setRetryCount(0);
    hasCalledOnUp.current = false;
    setWarehouseStatus({ databricks: "pending", clickhouse: "pending" });
    setIsDataWarehouseUp(false);
    setIsWarehouseLoading(true);
    setIsWarehouseLoadError(false);
    if (!isClickhouseEnabled) {
      checkDatabricksStatus();
    }
    checkClickhouseStatus();
  }, [isClickhouseEnabled, checkDatabricksStatus, checkClickhouseStatus]);

  // Retry logic for Databricks — only when Databricks is the primary warehouse
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isClickhouseEnabled && retry && retryCount > 0 && retryCount <= maxRetries) {
      timer = setTimeout(() => {
        checkDatabricksStatus();
        setRetry(false);
      }, retryInterval * 1000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isClickhouseEnabled, retry, retryCount, maxRetries, retryInterval, checkDatabricksStatus]);

  return {
    isDataWarehouseUp,
    isWarehouseLoading,
    isWarehouseLoadError,
    restartDataWarehouseCluster,
    vantageLockedStatus,
    // Expose individual statuses for debugging/monitoring
    warehouseStatus,
  };
};

export { useDataWarehouseConfigurations };
